import React from 'react'
import { PropTypes, shape, string, object } from 'prop-types'
import { connect } from 'react-redux'
import { getProjects } from '../../store/HomePage/actionCreator'
import rndLogo from '../../images/RolloutDeployment-NoBack.png'
import { Button, Grid, Menu, MenuItem } from '@mui/material'
import apiConfig from '../../config/apiConfig'
import { getDomainName } from '../Utils/GetDomainName'
import HeaderTitle from '../Header/HeaderTitle'
import MrtReusableTable from '../Utils/MrtReusableTable'
import { handleFireflySendEvent } from '../Utils/FireFlyVnextHelpers'

const styles = {
  button: {
    color: '#191919',
    borderColor: '#191919',
  },
  h2: {
    margin: 0,
    fontWeight: 400,
  },
  logoDiv: {
    height: 150,
    width: 150,
  },
  rndLogoStyle: {
    margin: 'auto',
    display: 'block',
    maxHeight: '100%',
    maxWidth: '100%',
  },
  span: {
    fontWeight: 800,
  },
}

class HomePage extends React.Component {
  static propTypes = {
    classes: object,
    accessToken: PropTypes.string,
    userInfo: shape({
      email: string,
    }),
  }

  static defaultProps = {
    userInfo: {
      email: undefined,
    },
  }

  constructor(props) {
    super(props)
    this.state = {
      projectsList: [],
      loading: true,
      anchorElTools: null,
      anchorElSlo: null,
    }
    this.baseState = { ...this.state }
  }

  async componentDidMount() {
    this.setState({ loading: true })
    const { userInfo } = this.props
    if (userInfo.email !== '') {
      handleFireflySendEvent(
        'PAGE_VIEW',
        'Home Page',
        'Home Page Load',
        userInfo,
      )
    }
    await this.props.getProjects()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.projectsList.length) {
      this.setState({
        projectsList: JSON.parse(JSON.stringify(nextProps.projectsList)),
      })
      this.setState({ loading: false })
    }
  }

  handleClickTools = (event) => {
    this.setState({ anchorElTools: event.currentTarget })
  }

  handleCloseTools = () => {
    this.setState({ anchorElTools: null })
  }

  render() {
    const { anchorElTools } = this.state
    const { button, h2, logoDiv, rndLogoStyle, span } = styles

    const isExternalDomain =
      getDomainName() !== apiConfig.auth.externalDomain ? false : true

    const columns = [
      {
        accessorKey: 'location_id',
        header: 'T#',
        size: 60,
      },
      {
        accessorKey: 'health',
        header: 'Health',
        description:
          'Support: Red/Yellow -> send to R&D, Blue/Green -> normal support',
        size: 70,
      },
      {
        accessorKey: 'project_name',
        header: 'Project | Scope Name:',
        description:
          'Project identifier and scope name. Format: PROJECT_ID | Descriptive scope name with details about deployment phase, technology stack, and geographical coverage area.',
        size: 300,
        grow: true,
      },
      {
        accessorKey: 'start_date',
        header: 'Execution Period Start Date',
      },
      {
        accessorKey: 'end_date',
        header: 'Execution Period End Date',
      },
      {
        accessorKey: 'scheduled_status',
        header: 'Scheduled Status',
        description: 'Short test: Status type',
      },
      {
        accessorKey: 'status',
        header: 'Scope Status',
        size: 120,
      },
      {
        accessorKey: 'number_of_revisits',
        header: 'Number Of Revisits',
      },
      {
        accessorKey: 'work_start_date',
        header: 'Work Start Date',
        size: 130,
      },
      {
        accessorKey: 'work_end_date',
        header: 'Work End Date',
        size: 120,
      },
      {
        accessorKey: 'work_time_frame',
        header: 'Hours | Time Frame of Work',
        size: 210,
      },
      {
        accessorKey: 'percentage_complete',
        header: '% Complete',
        size: 110,
      },
      {
        accessorKey: 'conditional_sign_off_notes',
        header: 'Conditional Signoff Notes',
      },
      {
        accessorKey: 'period',
        header: 'Execution Period',
      },
      {
        accessorKey: 'delay_reason',
        header: 'Delay Reason',
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        size: 400,
      },
      {
        accessorKey: 'turnover',
        header: 'Turnover Date',
      },
      {
        accessorKey: 'go_live_date',
        header: 'Go Live Date',
      },
      {
        accessorKey: 'vendor_project_manager',
        header: 'Vendor PM',
      },
      {
        accessorKey: 'vendor_visit_dates',
        header: 'Vendor Visit Dates',
      },
      {
        accessorKey: 'construction_pm',
        header: 'Construction PM',
      },
      {
        accessorKey: 'tts_project_manager',
        header: 'TTS Project Manager',
        size: 200,
        grow: true,
      },
      {
        accessorKey: 'consolidation_vendor',
        header: 'Consolidation Vendor',
      },
      {
        accessorKey: 'installation_vendor',
        header: 'Installation Vendor',
      },
      {
        accessorKey: 'low_voltage_vendor',
        header: 'Low Voltage Vendor',
      },
      {
        accessorKey: 'deployment_support_vendor',
        header: 'Vendor Responsible for Scope',
      },
      {
        accessorKey: 'tts_project_team',
        header: 'TTS Execution Team',
      },
      {
        accessorKey: 'sheet_url',
        header: 'Smartsheet URL',
      },
      {
        accessorKey: 'sheet_name',
        header: 'Smartsheet Name',
      },
      {
        accessorKey: 'project_summary',
        header: 'Project Summary',
      },
      {
        accessorKey: 'store_action_required',
        header: 'Store Action Required',
      },
      {
        accessorKey: 'hardware_arriving',
        header: 'Hardware Arriving',
      },
      {
        accessorKey: 'access_areas',
        header: 'Access Areas',
      },
      {
        accessorKey: 'time_to_task',
        header: 'Time to Task',
      },
      {
        accessorKey: 'time_of_day',
        header: 'Time of Day',
      },
      {
        accessorKey: 'question_resource',
        header: 'Question Resource',
      },
      {
        accessorKey: 'question_address',
        header: 'Question Address',
      },
      {
        accessorKey: 'pos_scope',
        header: 'Project has POS Scope',
      },
      {
        accessorKey: 'vendors_have_pin',
        header: 'Vendors will have control room PIN',
      },
      {
        accessorKey: 'rd_pin',
        header: 'R&D Pin',
      },
      {
        accessorKey: 'scope_project_identification',
        header: 'Scope Project Identification',
      },
    ]
    const defaultColumns = [
      'location_id',
      'health',
      'project_name',
      'status',
      'work_start_date',
      'work_end_date',
      'work_time_frame',
      'percentage_complete',
      'tts_project_manager',
      'tts_project_team',
    ]

    // Function that returns grouped data for the expanded row (based on original MUIDataTable structure)
    const expandedRowGroups = (rowData) => [
      {
        title: 'Project Details',
        columns: [
          { header: 'T#', accessorKey: 'location_id' },
          { header: 'Health', accessorKey: 'health' },
          { header: 'Project | Scope Name', accessorKey: 'project_name' },
          { header: 'Execution Period', accessorKey: 'period' },
          { header: 'Scope Status', accessorKey: 'status' },
          { header: '% Complete', accessorKey: 'percentage_complete' },
          { header: 'Scheduled Status', accessorKey: 'scheduled_status' },
          {
            header: 'Conditional Signoff Notes',
            accessorKey: 'conditional_sign_off_notes',
          },
          { header: 'Delay Reason', accessorKey: 'delay_reason' },
        ],
        data: [
          {
            location_id: rowData.location_id,
            health: rowData.health,
            project_name: rowData.project_name,
            period: rowData.period,
            status: rowData.status,
            percentage_complete: rowData.percentage_complete,
            scheduled_status: rowData.scheduled_status,
            conditional_sign_off_notes: rowData.conditional_sign_off_notes,
            delay_reason: rowData.delay_reason,
          },
        ],
      },
      {
        title: 'Project Timelines',
        columns: [
          { header: 'Work Start Date', accessorKey: 'work_start_date' },
          { header: 'Work End Date', accessorKey: 'work_end_date' },
          { header: 'Execution Period Start Date', accessorKey: 'start_date' },
          { header: 'Execution Period End Date', accessorKey: 'end_date' },
          {
            header: 'Hours | Time Frame of Work',
            accessorKey: 'work_time_frame',
          },
          { header: 'Turnover Date', accessorKey: 'turnover' },
          { header: 'Go Live Date', accessorKey: 'go_live_date' },
          { header: 'Vendor Visit Dates', accessorKey: 'vendor_visit_dates' },
        ],
        data: [
          {
            work_start_date: rowData.work_start_date,
            work_end_date: rowData.work_end_date,
            start_date: rowData.start_date,
            end_date: rowData.end_date,
            work_time_frame: rowData.work_time_frame,
            turnover: rowData.turnover,
            go_live_date: rowData.go_live_date,
            vendor_visit_dates: rowData.vendor_visit_dates,
          },
        ],
      },
      {
        title: 'Contact Information',
        columns: [
          { header: 'Vendor PM', accessorKey: 'vendor_project_manager' },
          { header: 'TTS Execution Team', accessorKey: 'tts_project_team' },
          { header: 'TTS Project Manager', accessorKey: 'tts_project_manager' },
          { header: 'Construction PM', accessorKey: 'construction_pm' },
          {
            header: 'Consolidation Vendor',
            accessorKey: 'consolidation_vendor',
          },
          {
            header: 'Vendor Responsible for Scope',
            accessorKey: 'deployment_support_vendor',
          },
        ],
        data: [
          {
            vendor_project_manager: rowData.vendor_project_manager,
            tts_project_team: rowData.tts_project_team,
            tts_project_manager: rowData.tts_project_manager,
            construction_pm: rowData.construction_pm,
            consolidation_vendor: rowData.consolidation_vendor,
            deployment_support_vendor: rowData.deployment_support_vendor,
          },
        ],
      },
      {
        title: 'Project Summary',
        columns: [
          { header: 'Project Summary', accessorKey: 'project_summary' },
          {
            header: 'Store Action Required',
            accessorKey: 'store_action_required',
          },
          { header: 'Hardware Arriving', accessorKey: 'hardware_arriving' },
          { header: 'Access Areas', accessorKey: 'access_areas' },
          { header: 'Time to Task', accessorKey: 'time_to_task' },
          { header: 'Time of Day', accessorKey: 'time_of_day' },
          { header: 'Question Resource', accessorKey: 'question_resource' },
          { header: 'Question Address', accessorKey: 'question_address' },
          { header: 'Project has POS Scope', accessorKey: 'pos_scope' },
          {
            header: 'Vendors will have control room PIN',
            accessorKey: 'vendors_have_pin',
          },
        ],
        data: [
          {
            project_summary: rowData.project_summary,
            store_action_required: rowData.store_action_required,
            hardware_arriving: rowData.hardware_arriving,
            access_areas: rowData.access_areas,
            time_to_task: rowData.time_to_task,
            time_of_day: rowData.time_of_day,
            question_resource: rowData.question_resource,
            question_address: rowData.question_address,
            pos_scope: rowData.pos_scope,
            vendors_have_pin: rowData.vendors_have_pin,
          },
        ],
      },
      {
        title: 'Misc',
        columns: [
          { header: 'Smartsheet Link', accessorKey: 'sheet_name' },
          { header: 'Notes', accessorKey: 'notes' },
        ],
        data: [
          {
            sheet_name:
              rowData.sheet_url && rowData.sheet_name ? (
                <a
                  href={rowData.sheet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'none' }}
                >
                  {rowData.sheet_name}
                </a>
              ) : (
                rowData.sheet_name || '-'
              ),
            notes: rowData.notes,
          },
        ],
      },
    ]

    return (
      <>
        <HeaderTitle title="Home" />
        <Grid
          container
          direction="row"
          spacing={8}
          justifyContent="space-evenly"
          alignItems="center"
        >
          <Grid item style={logoDiv} size={{ md: 2 }}>
            <img
              alt="R&D logo"
              src={rndLogo}
              className={'rndLogo'}
              style={rndLogoStyle}
            />
          </Grid>
          <Grid
            item
            size={{ md: 6 }}
            className="missionStatementDiv"
            sx={{ textAlign: 'center' }}
          >
            <h2 className="missionStatementText" style={h2}>
              With the help from our partners, we make Target the best in retail
              by&nbsp;
              <span className="missionStatementBold" style={span}>
                delivering technology
              </span>
              &nbsp;for our Guests and Team Members with&nbsp;
              <span className="missionStatementBold" style={span}>
                minimal disruption
              </span>
              . We&nbsp;
              <span className="missionStatementBold" style={span}>
                focus on Guests
              </span>
              &nbsp;over projects and over process.
            </h2>
          </Grid>
          {!isExternalDomain ? (
            <Grid item size={{ md: 2 }}>
              <Button
                aria-controls="home-page"
                aria-haspopup="true"
                variant="outlined"
                sx={button}
                onClick={this.handleClickTools}
              >
                External Tools
              </Button>
              <Menu
                id="home-page"
                anchorEl={anchorElTools}
                keepMounted
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElTools)}
                onClose={this.handleCloseTools}
              >
                <MenuItem
                  component="a"
                  onClick={this.handleCloseTools}
                  href="https://app.smartsheet.com/sheets/rCXxJ8ChvfWG38Gc7Jq5mcXQqfVrVC3CH8fccPq1?view=grid"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Automation Validation Grid - R&D Automated Testing
                  Documentation
                </MenuItem>
                <MenuItem
                  component="a"
                  onClick={this.handleCloseTools}
                  href="https://app.smartsheet.com/sheets/HH76JGRwRmwcHcmXvjfc7wJG98rGCc2MmQHxp4c1?view=grid"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Device Configuration Grid - R&D Device Configuration
                  Documentation
                </MenuItem>
                <MenuItem
                  component="a"
                  onClick={this.handleCloseTools}
                  href="https://visualize.target.com/d/isuISJIWz/guest-kiosk-status?tab=queries&orgId=1&refresh=15m&var-StoreNum=76&var-excluded_serials=All"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  MAC Grafana Dashboard - MAC Kiosk health
                </MenuItem>
                <MenuItem
                  component="a"
                  onClick={this.handleCloseTools}
                  href="https://mickra.prod.target.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mickra - Endpoint device information
                </MenuItem>
                <MenuItem
                  component="a"
                  onClick={this.handleCloseTools}
                  href="https://saml.iam.target.com/affwebservices/public/saml2sso?SPID=https://saml-l2e7y32y.gc1.mist.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mist AP Portal - Mist access point information
                </MenuItem>
                <MenuItem
                  component="a"
                  onClick={this.handleCloseTools}
                  href="https://nps.prod.target.com//"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Network Programmability Services (NPS)
                </MenuItem>
                <MenuItem
                  component="a"
                  onClick={this.handleCloseTools}
                  href="https://picard.prod.target.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Picard (New POSCentral) - POS device information
                </MenuItem>
                <MenuItem
                  component="a"
                  onClick={this.handleCloseTools}
                  href="https://app.smartsheet.com/dashboards/wJ4hF5J24pwxvmFgwX94XQ2vHq2XV7PXHf8j8j91/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  R&D Software Engineering Portal - Automation Documentation
                </MenuItem>
                <MenuItem
                  component="a"
                  onClick={this.handleCloseTools}
                  href="https://visualize.target.com/d/000009165/store-stack-health?orgId=24&refresh=5m"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Store Stack Health Dashboard - Network device health
                  information
                </MenuItem>
              </Menu>
            </Grid>
          ) : (
            <Grid item size={{ md: 2 }}>
              {/* blank grid element needed formatting if no External Tools gets displayed. */}
            </Grid>
          )}
        </Grid>
        <br></br>
        <MrtReusableTable
          pageSize={[10, 50, 100]}
          pageTitle={'R&D Active Projects'}
          columns={columns}
          rowData={this.state.projectsList}
          defaultColumns={defaultColumns}
          loading={this.state.loading}
          loadingMessage="&nbsp;R&D Active Projects Loading..."
          enableExpanding={true}
          expandedRowGroups={expandedRowGroups}
        />
      </>
    )
  }
}

const mapStateToProps = (state) => {
  const { layout } = state
  const { headerTitle } = layout

  return {
    headerTitle: headerTitle,
    accessToken: state.auth.accessToken,
    userInfo: state.auth.userInfo,
    projectsList: state.homePage.projectsList,
  }
}

const mapDispatchToProps = {
  getProjects,
}

export default connect(mapStateToProps, mapDispatchToProps)(HomePage)
